import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedState from 'pinia-plugin-persistedstate'
import App from './App.vue'
import router from './router'
import * as Sentry from '@sentry/vue'
import { createAuth0Instance } from '@/auth0'
import BaseBlock from '@/components/BaseBlock.vue'
import BaseBackground from '@/components/BaseBackground.vue'
import BasePageHeading from '@/components/PublinkerPageHeading.vue'
import PublinkerSelect from '@/components/PublinkerSelect.vue'
import PublinkerCloudIcon from '@/components/PublinkerCloudIcon.vue'
import clickRipple from '@/directives/clickRipple'
import * as bootstrap from 'bootstrap'
import { createLogger } from '@/shared/logger'
import 'flag-icons/css/flag-icons.min.css'

// Context Menu
import '@imengyu/vue3-context-menu/lib/vue3-context-menu.css'
import ContextMenu from '@imengyu/vue3-context-menu'

// Creating the central logger for this part of the application
const logger = createLogger('AuthFlow')

window.bootstrap = bootstrap

// Set mode (used for testing)
const mode = import.meta.env.MODE || import.meta.env.NODE_ENV

const pinia = createPinia()
pinia.use(piniaPluginPersistedState)

// Craft new application
const app = createApp(App)

// Global variables
app.config.globalProperties.$isProduction = import.meta.env.PROD
app.config.globalProperties.$isHomolog =
  import.meta.env.HOMOLOG || import.meta.env.MODE === 'homolog'
app.config.globalProperties.$isDevelopment = import.meta.env.DEV
app.config.globalProperties.$currentEnv = import.meta.env.MODE
app.config.globalProperties.$isTesting = import.meta.env.MODE == 'test'

if (mode === 'production') {
  // Sentry
  Sentry.init({
    app,
    dsn: 'https://be6f52019094975e0115a06983f3725c@o4508739336667136.ingest.us.sentry.io/4508767528222720',
    integrations: [Sentry.browserTracingIntegration({ router })],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.1,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [
      'localhost',
      /^http:\/\/127\.0\.0\.1:(8000|8001)/,
      /^https:\/\/(hlkl4tw3vd|cn4s36uzxh)\.execute-api\.us-east-1\.amazonaws\.com\/(prod|api)/,
      /^https:\/\/(z5oglzu9dc|6pujo0w649)\.execute-api\.us-east-1\.amazonaws\.com\/(homolog|api)/
    ],
    environment: import.meta.env.MODE
  })
}

// Extract information from the current URL
const extractPathInfoFromUrl = () => {
  const path = window.location.hash || ''
  logger.log('Current hash path:', path)

  // Expected URL pattern: /#/{site}/{page}
  // Extract the site and full path
  const match = path.match(/#\/([^/]+)(?:\/(.*))?/)

  if (!match) return null

  const site = match[1]
  const fullPath = match[2] || 'pricing' // Uses 'pricing' as default when path is not specified

  // Ignores if the site is 'login' or other special routes
  if (
    site === 'login' ||
    site === 'profile' ||
    site === 'checkout' ||
    site === 'billing'
  ) {
    return null
  }

  return { site, fullPath }
}

// Save URL information for use after login
const pathInfo = extractPathInfoFromUrl()
if (pathInfo) {
  logger.log('***** Saving pre-login path info:', pathInfo)
  // Save all necessary information to restore the path after login
  localStorage.setItem('publinker_login_target_site', pathInfo.site)
  localStorage.setItem('publinker_login_target_path', pathInfo.fullPath)
  localStorage.setItem('publinker_login_timestamp', Date.now().toString())
  localStorage.setItem('publinker_login_original_url', window.location.href)
}

// Auth
let auth0Params = {
  domain: 'dev-hqiwr-re.us.auth0.com',
  clientId:
    import.meta.env.VITE_AUTH0_CLIENT_ID || 'twCYKS5bHD7gjnCEdUddADV1B8KhAdls',
  authorizationParams: {
    redirect_uri:
      (window.location.hostname == 'localhost' ? 'http://' : 'https://') +
      window.location.host +
      '/#/login/pricing', // window.location.origin
    audience:
      import.meta.env.VITE_AUTH0_AUDIENCE || 'https://backend.publinker.com',
    // Não adicionar permissões ao token, iremos buscar separadamente
    scope: 'openid profile email'
  },
  // Redirect callback
  onRedirectCallback: appState => {
    try {
      logger.log('***** Auth0 redirect callback activated *****')
      logger.log('AppState received:', appState)

      // Get template store to access time window settings
      const templateStore = pinia.state.value.template

      // First check if appState contains a valid returnTo
      if (appState && appState.returnTo && appState.returnTo.includes('/')) {
        logger.log('***** Return URL found in appState:', appState.returnTo)
        const redirectUrl = `${window.location.origin}${appState.returnTo}`
        logger.log('***** Redirecting to appState URL:', redirectUrl)

        // This URL already contains the site and specific path
        window.location.href = redirectUrl
        return
      }

      // Check if there is recently saved path information
      const savedSite = localStorage.getItem('publinker_login_target_site')
      const savedPath = localStorage.getItem('publinker_login_target_path')
      const loginTimestamp = localStorage.getItem('publinker_login_timestamp')
      const originalUrl = localStorage.getItem('publinker_login_original_url')

      // Check if the information is recent using the store configuration
      let isRecentLogin = false

      if (
        loginTimestamp &&
        templateStore &&
        templateStore.recentLoginTimeWindow
      ) {
        const currentTime = Date.now()
        const elapsedTime = currentTime - parseInt(loginTimestamp, 10)
        isRecentLogin = elapsedTime < templateStore.recentLoginTimeWindow
      }

      logger.log('***** Saved site information:', {
        savedSite,
        savedPath,
        loginTimestamp,
        isRecentLogin,
        originalUrl,
        timeWindow: templateStore ? templateStore.recentLoginTimeWindow : 'N/A'
      })

      if (savedSite && isRecentLogin) {
        logger.log('***** Recent login site and path found')
        logger.log('***** Site:', savedSite)
        logger.log('***** Path:', savedPath || 'pricing (default)')

        // Does not clear data here as it may be needed in the PublinkerBackend component
        // to determine the exact path after loading permissions

        // Redirect to the original site and path
        const redirectPath = savedPath || 'pricing'
        const redirectUrl = `${window.location.origin}/#/${savedSite}/${redirectPath}`
        logger.log('***** Redirecting to complete URL:', redirectUrl)

        // Force redirect to the original URL with the specific path
        window.location.href = redirectUrl
        return
      }

      // If there is no saved site or it's not recent, follow the normal flow
      logger.log('***** No saved site or not recent, following default flow')
      window.history.replaceState({}, document.title, appState?.returnTo || '/')
    } catch (error) {
      console.error('Error in onRedirectCallback:', error)
      // In case of error, follow the default flow
      window.history.replaceState({}, document.title, appState?.returnTo || '/')
    }
  }
}
if (mode === 'test') {
  auth0Params = {
    useCookiesForTransactions: false,
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    cacheLocation: 'localstorage',
    domain: 'dev-hqiwr-re.us.auth0.com',
    clientId:
      import.meta.env.VITE_AUTH0_CLIENT_ID ||
      'twCYKS5bHD7gjnCEdUddADV1B8KhAdls',
    authorizationParams: {
      redirect_uri: 'http://localhost:5177/#/login/pricing',
      audience:
        import.meta.env.VITE_AUTH0_AUDIENCE || 'https://backend.publinker.com',
      prompt: 'none',
      // Não adicionar permissões ao token, iremos buscar separadamente
      scope: 'openid profile email'
    },
    // Keep the handler for test mode as well
    onRedirectCallback: auth0Params.onRedirectCallback
  }
}

// Create and use the instance of Auth0 singleton
const auth0Instance = createAuth0Instance(auth0Params)
app.use(auth0Instance)

// Register global components
app.component('BaseBlock', BaseBlock)
app.component('BaseBackground', BaseBackground)
app.component('BasePageHeading', BasePageHeading)
app.component('PublinkerSelect', PublinkerSelect)
app.component('PublinkerCloudIcon', PublinkerCloudIcon)

// Register global directives
app.directive('click-ripple', clickRipple)

// Use Pinia and Vue Router
app.use(pinia)
app.use(router)
app.use(ContextMenu)

// Register MSW for tests
async function enableMocking() {
  if (mode !== 'test') {
    return
  }

  const { worker } = await import('./mocks/browser')

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  window.$MSW_WORKER = worker
  return worker.start({ onUnhandledRequest: 'warn' })
}
enableMocking().then(() => {
  app.mount('#app')
})
